import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  User,
  Briefcase,
  Layers,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Building,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import {
  fetchPartnerServices,
  fetchServiceTypes,
  fetchLocalBazaarCategories,
  updatePartnerProfile,
  fetchPartnerUserById,
} from "../../api/partner/partnerApi";
import {
  updatePartnerProfileField,
  setServicesList,
  setLoadingServices,
  setSelectedService,
  setSubCategoriesList,
  setLoadingSubCategories,
  setPartnerLoading,
  setPartnerError,
} from "../../features/partnerOnboarding/partnerOnboardingSlice";
import { loginSuccess } from "../../features/auth/authSlice";
import { showToast } from "../../components/common/notification/Notification";
import PartnerLayout from "../../components/partner/PartnerLayout";
import LocationSelector from "../../components/customer/LocationSelector";
import "./PartnerPages.css";

export const PartnerOnboarding = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authUser = useSelector((state) => state.auth.user);
  const token = authUser?.accessToken || authUser?.token || authUser?.refreshToken;
  const userId = authUser?.user?.id || authUser?.userDetails?.id;

  const partnerState = useSelector((state) => state.partnerOnboarding);
  const { services, selectedService, subCategories, loadingServices, loadingSubCategories } = partnerState;

  // 1 = Basic Details (Step 3), 2 = Location Info (Step 4), 3 = Select Service (Step 5)
  const [activeSubStep, setActiveSubStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Form state matching mobile CompleteProfile
  const [formData, setFormData] = useState({
    first_name: authUser?.user?.first_name || partnerState.profile.first_name || "",
    last_name: authUser?.user?.last_name || partnerState.profile.last_name || "",
    email_address: authUser?.user?.email_address || authUser?.user?.email || partnerState.profile.email_address || "",
    mobile_number: authUser?.user?.mobile_number || partnerState.profile.mobile_number || "",
    city: authUser?.user?.cities?.[0]?.name || partnerState.profile.city || "",
    state: authUser?.user?.cities?.[0]?.state?.name || partnerState.profile.state || "",
    service_id: authUser?.user?.service_id ? String(authUser.user.service_id) : (partnerState.profile.service_id || ""),
    sub_category_id: partnerState.profile.sub_category_id || "",
  });

  const [locationValue, setLocationValue] = useState({
    country: "",
    state: "",
    city: "",
    area: "",
  });
  const [locationOptions, setLocationOptions] = useState({});

  // Load existing profile from backend
  useEffect(() => {
    if (token && userId) {
      fetchPartnerUserById(token, userId)
        .then((res) => {
          if (res?.user) {
            const u = res.user;
            setFormData((prev) => ({
              ...prev,
              first_name: u.first_name || prev.first_name,
              last_name: u.last_name || prev.last_name,
              email_address: u.email_address || prev.email_address,
              mobile_number: u.mobile_number || prev.mobile_number,
              service_id: u.service_id ? String(u.service_id) : prev.service_id,
              city: u.cities?.[0]?.name || prev.city,
              state: u.cities?.[0]?.state?.name || prev.state,
            }));
          }
        })
        .catch((e) => console.warn("Could not fetch user profile details:", e));
    }
  }, [token, userId]);

  // Fetch Services from backend
  useEffect(() => {
    if (!services || services.length === 0) {
      dispatch(setLoadingServices(true));
      fetchPartnerServices(token)
        .then((res) => {
          const list = res?.services || res?.data || [];
          dispatch(setServicesList(list));
        })
        .catch((err) => {
          console.error("Error fetching services:", err);
          dispatch(setLoadingServices(false));
        });
    }
  }, [services, token, dispatch]);

  const handleServiceSelect = (service) => {
    const sId = String(service.id);
    dispatch(setSelectedService(service));
    setFormData((prev) => ({ ...prev, service_id: sId, sub_category_id: "" }));

    const serviceName = String(service?.name || "").toLowerCase();

    dispatch(setLoadingSubCategories(true));

    if (serviceName.includes("local bazaar") || sId === "4") {
      fetchLocalBazaarCategories(token)
        .then((res) => {
          const cats = res?.localBazaarCategories || res?.categories || res?.data || [];
          dispatch(setSubCategoriesList(cats));
        })
        .catch((err) => {
          console.error("Error fetching bazaar categories:", err);
          dispatch(setSubCategoriesList([]));
        });
    } else if (serviceName.includes("service") || sId === "3") {
      fetchServiceTypes(token)
        .then((res) => {
          const types = res?.allServiceCategories || res?.serviceTypes || res?.data || res?.services || [];
          dispatch(setSubCategoriesList(types));
        })
        .catch((err) => {
          console.error("Error fetching service types:", err);
          dispatch(setSubCategoriesList([]));
        });
    } else {
      dispatch(setSubCategoriesList([]));
      dispatch(setLoadingSubCategories(false));
    }
  };

  const handleSubCategorySelect = (subCat) => {
    const subCatId = String(subCat.id);
    setFormData((prev) => ({ ...prev, sub_category_id: subCatId }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (nextLoc) => {
    setLocationValue(nextLoc);
  };

  const handleLocationOptionsChange = (opts) => {
    setLocationOptions(opts);
    if (opts.selectedCity?.name) {
      setFormData((prev) => ({
        ...prev,
        city: opts.selectedCity.name,
        state: opts.selectedState?.name || prev.state,
      }));
    }
  };

  const handleNextFromBasic = (e) => {
    e.preventDefault();
    if (!formData.first_name.trim()) {
      showToast("Please enter your first name.", { type: "error" });
      return;
    }
    if (!formData.last_name.trim()) {
      showToast("Please enter your last name.", { type: "error" });
      return;
    }
    if (!formData.email_address.trim()) {
      showToast("Please enter your email address.", { type: "error" });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email_address.trim())) {
      showToast("Please enter a valid email address.", { type: "error" });
      return;
    }
    setActiveSubStep(2);
  };

  const handleNextFromLocation = (e) => {
    if (e) e.preventDefault();
    if (!locationValue.state && !locationOptions.selectedState && !formData.state) {
      showToast("Please select your state.", { type: "error" });
      return;
    }
    if (!locationValue.city && !locationOptions.selectedCity && !formData.city) {
      showToast("Please select your city.", { type: "error" });
      return;
    }
    setActiveSubStep(3);
  };

  const handleSaveAndProceedToPlans = async (e) => {
    if (e) e.preventDefault();

    if (!formData.service_id) {
      showToast("Please select the service you provide.", { type: "error" });
      return;
    }

    const sId = String(formData.service_id);
    if ((sId === "3" || sId === "4") && subCategories.length > 0 && !formData.sub_category_id) {
      showToast("Please select a sub-category for your service.", { type: "error" });
      return;
    }

    setSaving(true);
    dispatch(setPartnerLoading(true));

    const selectedCityName = locationOptions?.selectedCity?.name || formData.city || "Noida";
    const selectedStateName = locationOptions?.selectedState?.name || formData.state || "Uttar Pradesh";

    const payload = {
      id: userId || Number(authUser?.user?.id),
      first_name: formData.first_name.trim(),
      last_name: formData.last_name?.trim() || "",
      email_address: formData.email_address.trim(),
      mobile_number: formData.mobile_number,
      whatsapp_mobile_number: formData.mobile_number,
      city: selectedCityName,
      state: selectedStateName,
      service_id: String(formData.service_id),
      ...(sId === "3" && formData.sub_category_id ? { all_services_category_id: formData.sub_category_id } : {}),
      ...(sId === "4" && formData.sub_category_id ? { local_bazaar_category_id: formData.sub_category_id } : {}),
    };

    try {
      const response = await updatePartnerProfile(token, payload);

      if (response?.statusType === "SUCCESS" || response?.status === "SUCCESS") {
        showToast("Partner profile updated successfully!", { type: "success" });
        dispatch(updatePartnerProfileField(payload));

        if (response.user) {
          dispatch(loginSuccess({ ...authUser, user: response.user }));
        }

        // Proceed to Step 6 (Select Plan)
        navigate("/partner/subscription", { state: { serviceId: formData.service_id } });
      } else {
        const errorMsg = response?.text || response?.message || "Failed to save profile. Please check the fields.";
        showToast(errorMsg, { type: "error" });
        dispatch(setPartnerError(errorMsg));
      }
    } catch (err) {
      console.error("Partner editProfile error:", err);
      const msg = err?.response?.data?.text || err?.response?.data?.message || "Error saving profile. Please try again.";
      showToast(msg, { type: "error" });
      dispatch(setPartnerError(msg));
    } finally {
      setSaving(false);
      dispatch(setPartnerLoading(false));
    }
  };

  // Step 3: Basic Details (activeSubStep 1)
  // Step 4: Location Info (activeSubStep 2)
  // Step 5: Select Service (activeSubStep 3)
  const currentStepIndex = activeSubStep === 1 ? 3 : activeSubStep === 2 ? 4 : 5;

  const stepTitles = {
    1: {
      title: "Basic Contact Details",
      subtitle: "Enter your personal contact information to set up your primary partner account.",
    },
    2: {
      title: "Location Information",
      subtitle: "Select your country, state, city, and area where you offer your partner services.",
    },
    3: {
      title: "Select Your Service Domain",
      subtitle: "Choose the primary business sector that represents your services on All Home Desire.",
    },
  };

  return (
    <PartnerLayout
      currentStepIndex={currentStepIndex}
      title={stepTitles[activeSubStep].title}
      subtitle={stepTitles[activeSubStep].subtitle}
      visualTitle="High-Growth B2B Partner Portal"
      visualSubtitle="Join India's verified network of property advisors, interior designers, and home service providers."
    >
      <div className="partner-form-container">
        {/* Step Navigation Pill Tabs */}
        <div className="partner-step-pills">
          <button
            type="button"
            className={`partner-step-pill ${activeSubStep === 1 ? "partner-step-pill--active" : ""}`}
            onClick={() => setActiveSubStep(1)}
          >
            <User size={15} />
            <span>1. Basic Details</span>
          </button>
          <button
            type="button"
            className={`partner-step-pill ${activeSubStep === 2 ? "partner-step-pill--active" : ""}`}
            onClick={() => {
              if (!formData.first_name.trim()) {
                showToast("Please enter your first name first.", { type: "error" });
                return;
              }
              setActiveSubStep(2);
            }}
          >
            <MapPin size={15} />
            <span>2. Location Info</span>
          </button>
          <button
            type="button"
            className={`partner-step-pill ${activeSubStep === 3 ? "partner-step-pill--active" : ""}`}
            onClick={() => {
              if (!formData.first_name.trim()) {
                showToast("Please complete basic details first.", { type: "error" });
                return;
              }
              setActiveSubStep(3);
            }}
          >
            <Layers size={15} />
            <span>3. Select Service</span>
          </button>
        </div>

        {/* SUB-STEP 1: BASIC DETAILS (Step 3) */}
        {activeSubStep === 1 && (
          <form onSubmit={handleNextFromBasic} className="partner-form partner-step-pane">
            <div className="partner-form-grid">
              <div className="partner-form__group">
                <label className="partner-form__label" htmlFor="partner-firstname">
                  First Name <span className="partner-required">*</span>
                </label>
                <div className="partner-input-wrap">
                  <span className="partner-input-icon">
                    <User size={18} />
                  </span>
                  <input
                    id="partner-firstname"
                    type="text"
                    name="first_name"
                    placeholder="Enter first name"
                    className="partner-input"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="partner-form__group">
                <label className="partner-form__label" htmlFor="partner-lastname">
                  Last Name
                </label>
                <div className="partner-input-wrap">
                  <span className="partner-input-icon">
                    <User size={18} />
                  </span>
                  <input
                    id="partner-lastname"
                    type="text"
                    name="last_name"
                    placeholder="Enter last name"
                    className="partner-input"
                    value={formData.last_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="partner-form__group">
              <label className="partner-form__label" htmlFor="partner-email">
                Email Address <span className="partner-required">*</span>
              </label>
              <div className="partner-input-wrap">
                <span className="partner-input-icon">
                  <Mail size={18} />
                </span>
                <input
                  id="partner-email"
                  type="email"
                  name="email_address"
                  placeholder="name@business.com"
                  className="partner-input"
                  value={formData.email_address}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="partner-form__group">
              <label className="partner-form__label" htmlFor="partner-mobile-display">
                Mobile Number
              </label>
              <div className="partner-input-wrap">
                <span className="partner-input-icon">
                  <Phone size={18} />
                </span>
                <span className="partner-input-prefix">+91</span>
                <input
                  id="partner-mobile-display"
                  type="text"
                  name="mobile_number"
                  className="partner-input"
                  value={formData.mobile_number}
                  disabled
                />
              </div>
            </div>

            <div className="partner-btn-group mt-4">
              <button type="submit" className="partner-btn partner-btn--primary partner-btn--lg w-100">
                <span>Continue to Location Info</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </form>
        )}

        {/* SUB-STEP 2: LOCATION INFORMATION (Step 4) */}
        {activeSubStep === 2 && (
          <form onSubmit={handleNextFromLocation} className="partner-form partner-step-pane">
            <div className="partner-form__group">
              <label className="partner-form__label">
                Operating Location <span className="partner-required">*</span>
              </label>
              <p className="text-muted mb-3" style={{ fontSize: "0.8125rem" }}>
                Select the state and city where you provide your services.
              </p>
              <LocationSelector
                value={locationValue}
                onChange={handleLocationChange}
                onOptionsChange={handleLocationOptionsChange}
                levels={["state", "city"]}
              />
              {(locationOptions?.selectedCity || locationOptions?.selectedState || formData.city || formData.state) && (
                <div className="partner-location-selected-badge mt-3 p-2 bg-light rounded-3 text-primary d-flex align-items-center gap-2" style={{ fontSize: "0.8125rem", fontWeight: 600 }}>
                  <MapPin size={15} />
                  <span>
                    Selected: {locationOptions?.selectedCountry?.name || "India"} → {locationOptions?.selectedState?.name || formData.state || ""} → {locationOptions?.selectedCity?.name || formData.city || ""}
                  </span>
                </div>
              )}
            </div>

            <div className="partner-btn-group mt-4">
              <button
                type="button"
                className="partner-btn partner-btn--outline"
                onClick={() => setActiveSubStep(1)}
              >
                <ArrowLeft size={16} />
                <span>Back</span>
              </button>
              <button type="submit" className="partner-btn partner-btn--primary">
                <span>Continue to Select Service</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </form>
        )}

        {/* SUB-STEP 3: SELECT SERVICE (Step 5) */}
        {activeSubStep === 3 && (
          <div className="partner-step-pane">
            <div className="partner-form__group mb-4">
              <label className="partner-form__label mb-3">
                What service do you provide? <span className="partner-required">*</span>
              </label>

              {loadingServices ? (
                <div className="partner-loading-state py-4 text-center">
                  <div className="spinner-border text-primary mb-2" role="status" />
                  <p className="text-muted">Loading partner services...</p>
                </div>
              ) : (
                <div className="partner-services-grid">
                  {services.map((service) => {
                    const isSelected = String(formData.service_id) === String(service.id);
                    return (
                      <div
                        key={service.id}
                        className={`partner-service-card ${isSelected ? "partner-service-card--selected" : ""}`}
                        onClick={() => handleServiceSelect(service)}
                      >
                        <div className="partner-service-card__header">
                          <div className="partner-service-card__icon">
                            <Briefcase size={20} />
                          </div>
                          {isSelected && (
                            <span className="partner-service-card__badge">
                              <CheckCircle2 size={16} />
                            </span>
                          )}
                        </div>
                        <h4 className="partner-service-card__title">{service.name}</h4>
                        <p className="partner-service-card__desc">
                          {service.description || `Professional ${service.name} services for buyers and homeowners`}
                        </p>
                        <div className="partner-service-card__cta">
                          <span>{isSelected ? "Selected ✓" : "Select Service →"}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sub-categories selection if service has specialization types (e.g. Services / Local Bazaar) */}
            {(String(formData.service_id) === "3" || String(formData.service_id) === "4" || (subCategories && subCategories.length > 0)) && (
              <div className="partner-form__group mb-4">
                <label className="partner-form__label">
                  Select Your Service Specialty / Sub-Category {(String(formData.service_id) === "3" || String(formData.service_id) === "4") && <span className="partner-required">*</span>}
                </label>
                {loadingSubCategories ? (
                  <div className="partner-loading-state py-3 text-center">
                    <div className="spinner-border spinner-border-sm text-primary mb-2" role="status" />
                    <p className="text-muted small">Loading sub-categories...</p>
                  </div>
                ) : subCategories && subCategories.length > 0 ? (
                  <div className="partner-subcat-chips">
                    {subCategories.map((item) => {
                      const isSubSelected = String(formData.sub_category_id) === String(item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          className={`partner-chip ${isSubSelected ? "partner-chip--selected" : ""}`}
                          onClick={() => handleSubCategorySelect(item)}
                        >
                          {isSubSelected && <CheckCircle2 size={14} />}
                          <span>{item.name || item.title}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted small">No specific sub-categories required for this service domain.</p>
                )}
              </div>
            )}

            <div className="partner-btn-group mt-4">
              <button
                type="button"
                className="partner-btn partner-btn--outline"
                onClick={() => setActiveSubStep(2)}
                disabled={saving}
              >
                <ArrowLeft size={16} />
                <span>Back</span>
              </button>
              <button
                type="button"
                className="partner-btn partner-btn--primary partner-btn--lg"
                onClick={handleSaveAndProceedToPlans}
                disabled={!formData.service_id || saving}
              >
                {saving ? (
                  <span className="partner-spinner">Saving Profile...</span>
                ) : (
                  <>
                    <span>Proceed to Service Plans</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </PartnerLayout>
  );
};

export default PartnerOnboarding;
